import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient, aiApi } from "../api/client";
import type { EvaluationCriteria, EvaluationDetail, AiInsight } from "../api/types";
import Card from "../components/Card";
import { useAuth } from "../context/AuthContext";
import { exportToPDF, exportToCSV, prepareDetailedEvaluationForExport } from "../utils/export";

function EvaluationReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const [evaluation, setEvaluation] = useState<EvaluationDetail | null>(null);
  const [criteria, setCriteria] = useState<EvaluationCriteria[]>([]);
  const [ai, setAi] = useState<AiInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Fetch evaluation and criteria
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const [evaluationRes, criteriaRes] = await Promise.all([
          apiClient.get<EvaluationDetail>(`/evaluations/${id}`),
          apiClient.get<{ criteria: EvaluationCriteria[] }>("/evaluation-criteria"),
        ]);
        setEvaluation(evaluationRes as EvaluationDetail);
        setCriteria(criteriaRes.criteria || []);
        setError(null);
      } catch (err: any) {
        setError(err.message || "Failed to load evaluation report");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const criteriaMap = useMemo(() => {
    const map: Record<string, EvaluationCriteria> = {};
    criteria.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [criteria]);

  const averageScore = useMemo(() => {
    if (!evaluation || !evaluation.scores) return null;
    const values = Object.values(evaluation.scores || {});
    if (!values.length) return null;
    return Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(2));
  }, [evaluation]);

  const loadAi = async () => {
    if (!evaluation) return;
    try {
      setAiLoading(true);
      setAiError(null);
      const insight = await aiApi.summarizeIndividual(evaluation.id);
      setAi(insight);
    } catch (err: any) {
      setAiError(err.message || "Failed to load AI insight");
    } finally {
      setAiLoading(false);
    }
  };

  const handleExportPDF = () => {
    exportToPDF(`Evaluation_Report_${evaluation?.id.substring(0, 8)}`);
  };

  const handleExportCSV = () => {
    if (!evaluation) return;
    const data = prepareDetailedEvaluationForExport(evaluation, criteria, "individual");
    exportToCSV(data, `Evaluation_Report_${evaluation.id.substring(0, 8)}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading evaluation report...</div>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error || "Evaluation not found"}
      </div>
    );
  }

  const statusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "bg-yellow-100 text-yellow-700";
      case "SUBMITTED":
        return "bg-blue-100 text-blue-700";
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const typeColor = (type: string) => {
    switch (type) {
      case "MANAGER":
        return "bg-purple-100 text-purple-700";
      case "PEER":
        return "bg-orange-100 text-orange-700";
      case "SELF":
        return "bg-teal-100 text-teal-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Evaluation Report</h2>
          <p className="text-sm text-slate-500 mt-1">Detailed view with AI insights</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors print:hidden"
            title="Export to CSV"
          >
            📊 CSV
          </button>
          <button
            onClick={handleExportPDF}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors print:hidden"
            title="Export to PDF"
          >
            📄 PDF
          </button>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor(evaluation.status)}`}
          >
            {evaluation.status}
          </span>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${typeColor(evaluation.evaluationType)}`}
          >
            {evaluation.evaluationType}
          </span>
        </div>
      </div>

      {/* Summary */}
      <Card title="Summary">
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-slate-500">Employee ID</p>
            <p className="font-mono text-slate-800">{evaluation.userId}</p>
          </div>
          <div>
            <p className="text-slate-500">Evaluator ID</p>
            <p className="font-mono text-slate-800">{evaluation.evaluatorId}</p>
          </div>
          <div>
            <p className="text-slate-500">Period</p>
            <p className="text-slate-800">{evaluation.periodId}</p>
          </div>
          <div>
            <p className="text-slate-500">Created</p>
            <p className="text-slate-800">{new Date(evaluation.createdAt).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-slate-500">Submitted</p>
            <p className="text-slate-800">
              {evaluation.submittedAt ? new Date(evaluation.submittedAt).toLocaleString() : "-"}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Completed</p>
            <p className="text-slate-800">
              {evaluation.completedAt ? new Date(evaluation.completedAt).toLocaleString() : "-"}
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4">
          <div>
            <p className="text-slate-500 text-sm">Overall Score</p>
            <p className="text-3xl font-bold text-indigo-600">
              {evaluation.overallScore ?? averageScore ?? "-"}
              <span className="text-base text-slate-500"> / 5</span>
            </p>
          </div>
          {averageScore !== null && averageScore !== undefined && (
            <div>
              <p className="text-slate-500 text-sm">Average (computed)</p>
              <p className="text-lg font-semibold text-slate-800">{averageScore}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Scores */}
      <Card title="Scores by Criteria">
        <div className="divide-y divide-slate-200">
          {Object.entries(evaluation.scores || {}).map(([criteriaId, score]) => {
            const c = criteriaMap[criteriaId];
            return (
              <div key={criteriaId} className="py-3 grid md:grid-cols-4 gap-4 items-center">
                <div className="md:col-span-2">
                  <p className="font-semibold text-slate-900">{c?.name || criteriaId}</p>
                  {c?.description && <p className="text-sm text-slate-600 mt-1">{c.description}</p>}
                  {c?.category && (
                    <span className="mt-2 inline-block text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                      {c.category}
                    </span>
                  )}
                </div>
                <div className="font-mono text-lg text-slate-800">{score}</div>
                <div className="text-sm text-slate-500">Max: {c?.maxScore ?? 5}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Comments */}
      {evaluation.comments && (
        <Card title="Comments">
          <p className="text-slate-800 leading-relaxed whitespace-pre-line">
            {evaluation.comments}
          </p>
        </Card>
      )}

      {/* AI Insights */}
      <Card
        title="AI Insight"
        subtitle="Summarized strengths, development areas, and recommendations"
        actions={
          <button
            onClick={loadAi}
            disabled={aiLoading}
            className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
          >
            {aiLoading ? "Generating..." : "Generate AI Summary"}
          </button>
        }
      >
        {aiError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm">
            {aiError}
          </div>
        )}

        {!ai && !aiLoading && (
          <p className="text-sm text-slate-600">Click "Generate AI Summary" to view insights.</p>
        )}

        {ai && (
          <div className="space-y-4">
            <div>
              <p className="text-slate-500 text-sm">Summary</p>
              <p className="text-slate-800 mt-1 leading-relaxed">{ai.summary}</p>
            </div>

            {ai.strengths && ai.strengths.length > 0 && (
              <div>
                <p className="text-slate-500 text-sm">Strengths</p>
                <ul className="list-disc list-inside text-slate-800 mt-1 space-y-1">
                  {ai.strengths.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {ai.developmentAreas && ai.developmentAreas.length > 0 && (
              <div>
                <p className="text-slate-500 text-sm">Development Areas</p>
                <ul className="list-disc list-inside text-slate-800 mt-1 space-y-1">
                  {ai.developmentAreas.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {ai.recommendations && ai.recommendations.length > 0 && (
              <div>
                <p className="text-slate-500 text-sm">Recommendations</p>
                <ul className="list-disc list-inside text-slate-800 mt-1 space-y-1">
                  {ai.recommendations.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

export default EvaluationReport;
